import { useState } from "react";
import { CircleCheckBig, Phone, MessageCircle, MapPin, Clock } from "lucide-react";
import { waLink } from "../../lib/constants";
import useReveal from "../../hooks/useReveal";

const CHECKS = [
  "Avaliação clínica completa",
  "Discussão sobre sua história com derrame pleural",
  "Explicação detalhada dos procedimentos e opções",
  "Análise de cobertura de convênio",
  "Resposta a todas as suas dúvidas",
  "Orçamento personalizado quando necessário",
];

export default function Contato() {
  const { ref, isVisible } = useReveal();
  const [form, setForm] = useState({ nome: "", telefone: "", mensagem: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone) return;
    const msg = `Olá, meu nome é ${form.nome}. Telefone: ${form.telefone}. ${
      form.mensagem ? `Mensagem: ${form.mensagem}` : "Gostaria de agendar uma avaliação de derrame pleural com dr. Mateus."
    }`;
    window.open(waLink(msg), "_blank");
  };

  return (
    <section
      id="contato"
      ref={ref}
      className={`py-24 md:py-32 bg-gradient-to-br from-[#0D47A1] to-[#00897B] reveal-section ${
        isVisible ? "is-visible" : ""
      }`}
      data-testid="contato-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="text-white">
            <div className="inline-flex items-center bg-white/15 border border-white/25 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
              Agende Sua Avaliação
            </div>
            <h2 className="font-poppins font-bold text-3xl sm:text-4xl mb-6 leading-tight">
              Marque Sua Avaliação Agora
            </h2>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-6">
              Você está sentindo falta de ar ou descobriu derrame em exame e está aqui buscando
              respostas. Isso significa que você está tomando ação. Próximo passo: consulta com um
              Cirurgião Torácico que realmente entende derrame pleural.
            </p>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8">
              Não será rápido e superficial. Será conversa real, com tempo dedicado, com explicações
              claras. Você sai da consulta sabendo exatamente o que tem e o que precisa ser feito para
              respirar melhor.
            </p>
            <div className="space-y-3 mb-10">
              {CHECKS.map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CircleCheckBig size={18} className="text-white/90 flex-shrink-0" />
                  <span className="text-white/85 text-sm md:text-base">{c}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="flex items-start gap-3 bg-white/10 rounded-xl p-4 border border-white/15">
                <MapPin size={18} className="text-white/90 mt-0.5 flex-shrink-0" />
                <div className="text-white/90 text-sm">
                  <div className="font-semibold mb-0.5">Localização</div>
                  <div className="text-white/75">Feira de Santana, BA</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/10 rounded-xl p-4 border border-white/15">
                <Clock size={18} className="text-white/90 mt-0.5 flex-shrink-0" />
                <div className="text-white/90 text-sm">
                  <div className="font-semibold mb-0.5">Horário</div>
                  <div className="text-white/75">Seg a Sex · 8h–18h</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/10 rounded-xl p-4 border border-white/15">
                <Phone size={18} className="text-white/90 mt-0.5 flex-shrink-0" />
                <div className="text-white/90 text-sm">
                  <div className="font-semibold mb-0.5">WhatsApp</div>
                  <div className="text-white/75">(75) 98187-2440</div>
                </div>
              </div>
            </div>

            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contato-primary-cta"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0D47A1] font-poppins font-bold px-8 py-4 rounded-full hover:bg-gray-50 transition-all duration-200 shadow-lg hover:-translate-y-0.5"
            >
              <Phone size={18} />
              AGENDAR CONSULTA INICIAL
            </a>
            <p className="text-white/55 text-xs mt-3">Agenda disponível em até 72h</p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <h3 className="font-poppins font-bold text-xl text-[#212121] mb-2">Envie sua mensagem</h3>
            <p className="text-[#212121]/60 text-sm mb-6">
              Preencha e falaremos com você pelo WhatsApp em breve.
            </p>
            <form className="space-y-4" data-testid="contact-form" onSubmit={onSubmit}>
              <div>
                <label className="block text-sm font-semibold text-[#212121] mb-1.5">Seu nome *</label>
                <input
                  required
                  placeholder="Como prefere ser chamado(a)?"
                  data-testid="contact-name-input"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm"
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                  Telefone / WhatsApp *
                </label>
                <input
                  required
                  placeholder="(00) 00000-0000"
                  data-testid="contact-phone-input"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm"
                  type="tel"
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#212121] mb-1.5">
                  Mande sua mensagem (opcional)
                </label>
                <textarea
                  placeholder="Conte um pouco sobre sua situação..."
                  data-testid="contact-message-input"
                  rows="3"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[#212121] placeholder-gray-400 focus:outline-none focus:border-[#0D47A1] focus:ring-2 focus:ring-[#0D47A1]/10 transition-all text-sm resize-none"
                  value={form.mensagem}
                  onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold py-4 rounded-xl hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
              >
                <MessageCircle size={18} />
                ENVIAR PELO WHATSAPP
              </button>
            </form>
            <p className="text-[#212121]/45 text-xs text-center mt-4">
              Seus dados são usados apenas para agendamento. Nunca serão compartilhados.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
